import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, IndianRupee, ClipboardList, Users, Flame, CalendarRange, Clock3, Timer } from "lucide-react";

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const [t, setT] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics/summary").then((r) => setS(r.data)).catch(() => {});
    api.get("/admin/analytics/trends").then((r) => setT(r.data)).catch(() => {});
  }, []);
  if (!s) return <div className="text-gray-400">Loading…</div>;

  const stats = [
    { l: "Today's Revenue", v: `₹${s.today_revenue}`, icon: IndianRupee, tid: "stat-today-rev" },
    { l: "Today's Orders", v: s.today_orders, icon: TrendingUp, tid: "stat-today-orders" },
    { l: "Pending Orders", v: s.pending_orders, icon: ClipboardList, tid: "stat-pending" },
    { l: "Customers", v: s.customers, icon: Users, tid: "stat-customers" },
  ];

  return (
    <div>
      <header className="mb-8">
        <div className="text-xs uppercase tracking-widest text-gray-400">Overview</div>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-1">Dashboard</h1>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {stats.map((st) => (
          <div key={st.l} data-testid={st.tid} className="qd-admin-card p-5">
            <div className="flex items-start justify-between">
              <div className="text-xs uppercase tracking-widest text-gray-400">{st.l}</div>
              <st.icon className="w-4 h-4 text-gray-300" aria-hidden />
            </div>
            <div className="font-display text-3xl font-bold mt-3">{st.v}</div>
          </div>
        ))}
      </div>

      {t && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="qd-admin-card p-6" data-testid="revenue-trend">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400 mb-4">
              <CalendarRange className="w-3.5 h-3.5" aria-hidden /> Revenue · last 14 days
            </div>
            <RevenueTrend data={t.revenue_by_day} />
          </div>

          <div className="qd-admin-card p-6" data-testid="top-items-30">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400 mb-4">
              <Flame className="w-3.5 h-3.5" aria-hidden /> Top items · last 30 days
            </div>
            <TopItems items={t.top_items} />
          </div>
        </div>
      )}

      {t?.fulfilment && (
        <div className="qd-admin-card p-6 mb-4" data-testid="fulfilment-times">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400 mb-4">
            <Timer className="w-3.5 h-3.5" aria-hidden /> Average fulfilment time · completed orders, last 30 days
          </div>
          <Fulfilment f={t.fulfilment} />
        </div>
      )}

      {t && (
        <div className="qd-admin-card p-6 mb-4" data-testid="hour-heatmap">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400 mb-4">
            <Clock3 className="w-3.5 h-3.5" aria-hidden /> Busy hours · orders by day & hour (last 14 days)
          </div>
          <Heatmap heat={t.heatmap} />
        </div>
      )}

      <div className="mt-4 text-sm text-gray-500">Total revenue all-time: <span className="font-semibold text-gray-800">₹{s.total_revenue}</span> · Total orders: {s.total_orders}</div>
    </div>
  );
}

// 14-day revenue bar trend. Pure flexbox bars — no chart library.
function RevenueTrend({ data }) {
  const max = Math.max(...data.map((d) => d.revenue), 1);
  const total = data.reduce((a, d) => a + d.revenue, 0);
  if (total === 0) return <div className="text-sm text-gray-400 py-8 text-center">No revenue in the last 14 days</div>;
  return (
    <div>
      <div className="flex items-end gap-1 h-36">
        {/* each column is h-full so the bar's % height resolves against the 9rem row */}
        {data.map((d) => (
          <div key={d.date} className="flex-1 h-full flex flex-col items-center justify-end gap-1 group" title={`${d.label}: ₹${d.revenue} · ${d.orders} order${d.orders === 1 ? "" : "s"}`}>
            <div className="text-[9px] text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">₹{Math.round(d.revenue)}</div>
            <div className="w-full rounded-t-md bg-[var(--qd-primary)] transition-colors group-hover:bg-[var(--qd-primary-dark,#c24a2e)]"
              style={{ height: `${Math.max((d.revenue / max) * 85, d.revenue > 0 ? 4 : 1)}%`, opacity: d.revenue > 0 ? 1 : 0.15 }} />
          </div>
        ))}
      </div>
      <div className="flex gap-1 mt-1">
        {data.map((d, i) => (
          <div key={d.date} className="flex-1 text-center text-[8px] text-gray-400">{i % 2 === 0 ? d.label.split(" ")[0] : ""}</div>
        ))}
      </div>
    </div>
  );
}

// Ranked top items with quantity bars.
function TopItems({ items }) {
  if (!items.length) return <div className="text-sm text-gray-400 py-8 text-center">No orders in the last 30 days</div>;
  const max = Math.max(...items.map((i) => i.qty), 1);
  return (
    <div className="space-y-2.5">
      {items.slice(0, 6).map((it, i) => (
        <div key={it.name}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="font-medium truncate mr-3">{i + 1}. {it.name}</span>
            <span className="text-gray-500 shrink-0 text-xs">{it.qty} sold · ₹{it.revenue}</span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full rounded-full bg-[var(--qd-charcoal)]" style={{ width: `${(it.qty / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Average time spent in each stage of a completed order, plus the total.
function fmtMin(m) {
  if (m == null) return "—";
  if (m < 1) return `${Math.round(m * 60)}s`;
  if (m < 60) return `${Math.round(m)}m`;
  return `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;
}
function Fulfilment({ f }) {
  if (!f.orders) return <div className="text-sm text-gray-400 py-6 text-center">No completed orders in the last 30 days</div>;
  const known = f.stages.filter((s) => s.avg_min != null);
  const max = Math.max(...known.map((s) => s.avg_min), 0.1);
  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-2 mb-4">
        <span className="font-display text-3xl font-bold" data-testid="avg-total-time">{fmtMin(f.avg_total_min)}</span>
        <span className="text-xs text-gray-400">avg per order · {f.orders} completed order{f.orders === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-2.5">
        {f.stages.map((s) => (
          <div key={s.key}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-600">{s.label}</span>
              <span className="font-semibold">{fmtMin(s.avg_min)}</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full bg-[var(--qd-primary)]"
                style={{ width: s.avg_min != null ? `${(s.avg_min / max) * 100}%` : "0%" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Day-of-week × hour grid; darker = busier. Hours are restaurant-local (IST).
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function Heatmap({ heat }) {
  const max = Math.max(...heat.flat(), 1);
  const label = (h) => (h === 0 ? "12am" : h === 12 ? "12pm" : h < 12 ? `${h}am` : `${h - 12}pm`);
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[560px]">
        {heat.map((row, d) => (
          <div key={d} className="flex items-center gap-1 mb-1">
            <div className="w-8 text-[10px] text-gray-400 shrink-0">{DOW[d]}</div>
            {row.map((v, h) => (
              <div key={h} title={`${DOW[d]} ${label(h)} — ${v} order${v === 1 ? "" : "s"}`}
                className="flex-1 h-5 rounded-[3px]"
                style={{ background: v === 0 ? "rgba(0,0,0,0.04)" : `rgba(224,93,61,${0.15 + 0.85 * (v / max)})` }} />
            ))}
          </div>
        ))}
        <div className="flex items-center gap-1 mt-1">
          <div className="w-8 shrink-0" />
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="flex-1 text-center text-[8px] text-gray-400">{h % 6 === 0 ? label(h) : ""}</div>
          ))}
        </div>
      </div>
    </div>
  );
}
